/**
 * Paradigm SDK — browser-side field encryption (WebCrypto API).
 *
 * Interoperable with sdk/crypto.py — same spec:
 *
 * v1 (legacy / read):  AES-256-GCM with PBKDF2 symmetric key
 *   KDF    : PBKDF2-HMAC-SHA256, 600,000 iterations, salt=userId (UTF-8)
 *   Cipher : AES-256-GCM, 12-byte random nonce, 16-byte tag
 *   AAD    : field name (UTF-8)
 *   Format : paradigm_enc:v1:<base64url(nonce[12] || ciphertext+tag)>
 *
 * v2 (current / write): ECIES — P-256 ECDH + HKDF-SHA256 + AES-256-GCM
 *   Key agreement : ECDH with ephemeral P-256 key + recipient P-256 public key
 *   KDF    : HKDF-SHA256, salt="paradigm-enc-v2", info=fieldName (UTF-8)
 *   Cipher : AES-256-GCM, 12-byte random nonce, 16-byte tag
 *   AAD    : field name (UTF-8)
 *   Format : paradigm_enc:v2:<base64url(
 *               ephemeral_pubkey_uncompressed[65]
 *             + nonce[12]
 *             + ciphertext+tag
 *            )>
 *
 * Key storage model:
 *   - Private key: generated in browser, wrapped (AES-GCM) with PBKDF2 key,
 *     stored on server — server can NEVER unwrap it.
 *   - Public key: stored on server in plain — 3PAs use it to encrypt.
 *   - Both keys held in memory (CryptoKey objects) after login.
 */

const PREFIX_V1 = 'paradigm_enc:v1:';
const PREFIX_V2 = 'paradigm_enc:v2:';
const PREFIX_V3 = 'paradigm_enc:v3:';
const PBKDF2_ITERATIONS = 600_000;
const NONCE_LEN = 12;
const PUBKEY_LEN = 65; // P-256 uncompressed point
const DEK_LEN = 32;    // AES-256 per-node data key (v3)
const enc = new TextEncoder();
const dec = new TextDecoder();

export function toBase64url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function fromBase64url(str) {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return Uint8Array.from(atob(padded), c => c.charCodeAt(0));
}

// ---------------------------------------------------------------------------
// Wrapping key (PBKDF2) — same as old v1 symmetric key
// ---------------------------------------------------------------------------

/**
 * Derive a 256-bit AES-GCM key from the user's password.
 * Used to wrap/unwrap the private key stored on the server.
 * Also used to decrypt legacy v1 ciphertext.
 */
export async function deriveWrappingKey(password, userId) {
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(userId), iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}


// ---------------------------------------------------------------------------
// P-256 key pair generation and wrapping
// ---------------------------------------------------------------------------

/** Generate a new P-256 ECDH key pair (extractable). */
export async function generateKeyPair() {
  return crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

/** Export public key as 65-byte uncompressed point (Uint8Array). */
export async function exportPublicKey(publicKey) {
  return new Uint8Array(await crypto.subtle.exportKey('raw', publicKey));
}

/** Import a 65-byte uncompressed P-256 public key. */
export async function importPublicKey(bytes) {
  return crypto.subtle.importKey(
    'raw', bytes,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    []
  );
}

/** Wrap (AES-GCM encrypt) the private key. Returns base64url string. */
export async function wrapPrivateKey(privateKey, wrappingKey) {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', privateKey);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, wrappingKey, pkcs8);
  const combined = new Uint8Array(NONCE_LEN + ct.byteLength);
  combined.set(nonce);
  combined.set(new Uint8Array(ct), NONCE_LEN);
  return toBase64url(combined);
}

/** Unwrap the stored private key. Returns ECDH CryptoKey. */
export async function unwrapPrivateKey(wrapped, wrappingKey) {
  const raw = fromBase64url(wrapped);
  const pkcs8 = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: raw.slice(0, NONCE_LEN) },
    wrappingKey,
    raw.slice(NONCE_LEN)
  );
  return crypto.subtle.importKey(
    'pkcs8', pkcs8,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

// ---------------------------------------------------------------------------
// ECIES helpers
// ---------------------------------------------------------------------------

async function _hkdfAesKey(sharedSecretBits, fieldName) {
  const km = await crypto.subtle.importKey('raw', sharedSecretBits, { name: 'HKDF' }, false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('paradigm-enc-v2'), info: enc.encode(fieldName) },
    km,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---------------------------------------------------------------------------
// Field-level encrypt / decrypt
// ---------------------------------------------------------------------------

// Exported 2026-09-20 for plugin setup-config, which encrypts a secret to a
// PLUGIN's public key rather than the user's. Nothing about the algorithm
// changes: encrypting *to* a recipient needs only their public key, which is
// why that page never asks for a password and never touches encKeys.
export async function encryptFieldV2(value, fieldName, publicKey) {
  const ephemeral = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
  const ephemeralPubBytes = new Uint8Array(await crypto.subtle.exportKey('raw', ephemeral.publicKey));
  const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, ephemeral.privateKey, 256);
  const aesKey = await _hkdfAesKey(sharedBits, fieldName);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: enc.encode(fieldName) },
    aesKey,
    enc.encode(JSON.stringify(value))
  );
  const blob = new Uint8Array(PUBKEY_LEN + NONCE_LEN + ct.byteLength);
  blob.set(ephemeralPubBytes);
  blob.set(nonce, PUBKEY_LEN);
  blob.set(new Uint8Array(ct), PUBKEY_LEN + NONCE_LEN);
  return `${PREFIX_V2}${toBase64url(blob)}`;
}

/**
 * Decrypt a single encrypted field.
 * encKeys = { privateKey?: CryptoKey, wrappingKey?: CryptoKey }
 * - v2 needs privateKey
 * - v1 (legacy) needs wrappingKey
 */
export async function decryptField(ciphertext, fieldName, encKeys) {
  if (!isEncrypted(ciphertext)) return ciphertext;

  if (ciphertext.startsWith(PREFIX_V2)) {
    const { privateKey } = encKeys;
    if (!privateKey) throw new Error('privateKey required to decrypt v2');
    const raw = fromBase64url(ciphertext.slice(PREFIX_V2.length));
    const ephemeralPub = await importPublicKey(raw.slice(0, PUBKEY_LEN));
    const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: ephemeralPub }, privateKey, 256);
    const aesKey = await _hkdfAesKey(sharedBits, fieldName);
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: raw.slice(PUBKEY_LEN, PUBKEY_LEN + NONCE_LEN), additionalData: enc.encode(fieldName) },
      aesKey,
      raw.slice(PUBKEY_LEN + NONCE_LEN)
    );
    return JSON.parse(dec.decode(plaintext));
  }

  if (ciphertext.startsWith(PREFIX_V1)) {
    const { wrappingKey } = encKeys;
    if (!wrappingKey) throw new Error('wrappingKey required to decrypt v1');
    const raw = fromBase64url(ciphertext.slice(PREFIX_V1.length));
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: raw.slice(0, NONCE_LEN), additionalData: enc.encode(fieldName) },
      wrappingKey,
      raw.slice(NONCE_LEN)
    );
    return JSON.parse(dec.decode(plaintext));
  }

  return ciphertext;
}

export function isEncrypted(value) {
  // Any-version prefix check: ciphertext is self-describing, so recognition
  // must not depend on which versions this client can decrypt.
  return typeof value === 'string' && value.startsWith('paradigm_enc:');
}

/**
 * Field names in value_json whose values are Paradigm ciphertext.
 *
 * Ciphertext is self-describing (paradigm_enc:vN: prefix), so this needs no
 * schema. Use it instead of the schema's encrypted_fields list when deciding
 * what to decrypt — a schema version that revoked encryption no longer
 * declares the field, but legacy nodes still hold ciphertext.
 */
export function findEncryptedFields(valueJson) {
  if (!valueJson || typeof valueJson !== 'object') return [];
  return Object.keys(valueJson).filter((k) => isEncrypted(valueJson[k]));
}

/**
 * Prefix-driven decryption: decrypt every value carrying a paradigm_enc:
 * prefix, regardless of what the current schema version declares.
 *
 * The schema governs encryption on write; decryption must follow the data —
 * fields revoked from encrypted_fields in a newer schema version still hold
 * ciphertext on legacy nodes. Values whose key material is absent (or that
 * fail to decrypt) are left as ciphertext; never throws.
 *
 * encKeys = { privateKey, wrappingKey? } for v1/v2; dek for v3.
 */
export async function decryptValueJsonAuto(valueJson, encKeys, dek = null) {
  const fields = findEncryptedFields(valueJson);
  if (!fields.length) return valueJson;
  const result = { ...valueJson };
  for (const field of fields) {
    const val = result[field];
    try {
      if (val.startsWith(PREFIX_V3)) {
        if (dek) result[field] = await decryptFieldV3(val, field, dek);
      } else if (encKeys) {
        result[field] = await decryptField(val, field, encKeys);
      }
    } catch {
      // Leave as ciphertext — the UI masks values that still carry the prefix.
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// v3 — per-node envelope encryption (scoped sharing)
//
// A node's fields are sealed under one random per-node Data Encryption Key
// (DEK, AES-256-GCM). The DEK is wrapped (ECIES v2) to each principal allowed
// to read the node — the owner, plus each share recipient. Sharing one node
// hands over only that node's DEK (never the user's private key), so the
// recipient cannot decrypt the owner's other nodes.
// Wire-format matches sdk/crypto.py and ink/crypto.js v3.
// ---------------------------------------------------------------------------

/** Generate a random 32-byte AES-256 node Data Encryption Key. */
export function generateDek() {
  return crypto.getRandomValues(new Uint8Array(DEK_LEN));
}

async function _importDek(dek) {
  return crypto.subtle.importKey('raw', dek, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/** Encrypt a single field value under a per-node DEK (v3). */
export async function encryptFieldV3(value, fieldName, dek) {
  const key = await _importDek(dek);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: enc.encode(fieldName) },
    key, enc.encode(JSON.stringify(value))
  );
  const blob = new Uint8Array(NONCE_LEN + ct.byteLength);
  blob.set(nonce);
  blob.set(new Uint8Array(ct), NONCE_LEN);
  return `${PREFIX_V3}${toBase64url(blob)}`;
}

/** Decrypt a v3 ciphertext using the node DEK. */
export async function decryptFieldV3(ciphertext, fieldName, dek) {
  const key = await _importDek(dek);
  const raw = fromBase64url(ciphertext.slice(PREFIX_V3.length));
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: raw.slice(0, NONCE_LEN), additionalData: enc.encode(fieldName) },
    key, raw.slice(NONCE_LEN)
  );
  return JSON.parse(dec.decode(plaintext));
}

/** Wrap a node DEK for a principal's P-256 public key (reuses ECIES v2). */
export async function wrapDek(dek, publicKey) {
  return encryptFieldV2(toBase64url(dek), 'node_dek', publicKey);
}

/** Unwrap a node DEK with the user's private key. Returns Uint8Array. */
export async function unwrapDek(wrappedDek, privateKey) {
  const b64 = await decryptField(wrappedDek, 'node_dek', { privateKey });
  return fromBase64url(b64);
}

/** True if value_json still holds any v3 field for the given field list. */
export function hasV3Field(valueJson, fields) {
  return (fields || []).some(
    (f) => typeof valueJson?.[f] === 'string' && valueJson[f].startsWith(PREFIX_V3)
  );
}

/** Encrypt declared fields under a DEK (v3). Already-encrypted values skipped. */
export async function encryptValueJsonV3(valueJson, encryptedFields, dek) {
  if (!encryptedFields?.length) return valueJson;
  const result = { ...valueJson };
  for (const field of encryptedFields) {
    if (field in result && result[field] != null && !isEncrypted(result[field])) {
      result[field] = await encryptFieldV3(result[field], field, dek);
    }
  }
  return result;
}

/** Decrypt declared v3 fields using the node DEK. */
export async function decryptValueJsonV3(valueJson, encryptedFields, dek) {
  if (!encryptedFields?.length || !valueJson) return valueJson;
  const result = { ...valueJson };
  for (const field of encryptedFields) {
    if (typeof result[field] === 'string' && result[field].startsWith(PREFIX_V3)) {
      result[field] = await decryptFieldV3(result[field], field, dek);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Recovery code key escrow
// Spec: PBKDF2(normalized_code, userId, 300k, SHA-256) → AES-256-GCM key
//       enc_key_escrow = base64url(nonce[12] || AES-GCM(key, pkcs8_der))
// ---------------------------------------------------------------------------

const ESCROW_PBKDF2_ITERATIONS = 300_000;

/**
 * Derive a 256-bit AES-GCM wrapping key from a recovery code.
 * Uses fewer iterations than the password wrapping key because recovery codes
 * are cryptographically random (not user-chosen).
 */
export async function deriveEscrowKey(recoveryCode, userId) {
  const normalized = recoveryCode.replace(/-/g, '').toUpperCase();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(normalized), { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(userId), iterations: ESCROW_PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/** Wrap (AES-GCM encrypt) the private key under an escrow key. Returns base64url string. */
export async function wrapKeyForEscrow(privateKey, escrowKey) {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', privateKey);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, escrowKey, pkcs8);
  const combined = new Uint8Array(NONCE_LEN + ct.byteLength);
  combined.set(nonce);
  combined.set(new Uint8Array(ct), NONCE_LEN);
  return toBase64url(combined);
}

/** Unwrap an escrow-wrapped private key. Returns ECDH CryptoKey. */
export async function unwrapKeyFromEscrow(encKeyEscrow, escrowKey) {
  const raw = fromBase64url(encKeyEscrow);
  const pkcs8 = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: raw.slice(0, NONCE_LEN) },
    escrowKey,
    raw.slice(NONCE_LEN)
  );
  return crypto.subtle.importKey(
    'pkcs8', pkcs8,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

// ---------------------------------------------------------------------------
// Per-app delegated key wrapping
// Spec: ECIES v2 (same as field encryption) with field name 'enc_user_privkey'
//       Wraps the user's PKCS8 private key with the 3PA's P-256 public key so
//       the app can decrypt encrypted fields without knowing the user's password.
// ---------------------------------------------------------------------------

/**
 * Wrap the user's private key for a 3rd party app.
 * The app can decrypt the result with its own private key via the SDK.
 *
 * @param {CryptoKey} privateKey - User's ECDH private key (from encKeys)
 * @param {string} appPublicKeyBase64 - App's P-256 public key, base64url uncompressed
 * @returns {string} paradigm_enc:v2:... blob stored as enc_user_privkey on the EP
 */
export async function wrapPrivateKeyForApp(privateKey, appPublicKeyBase64) {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', privateKey);
  const pkcs8b64 = toBase64url(new Uint8Array(pkcs8));
  const appPubKey = await importPublicKey(fromBase64url(appPublicKeyBase64));
  return encryptFieldV2(pkcs8b64, 'enc_user_privkey', appPubKey);
}

/**
 * Unwrap a user's private key from the app-delegated blob.
 * Only used by SDK / testing — the browser only wraps, never unwraps via this path.
 *
 * @param {string} encUserPrivkey - The paradigm_enc:v2:... blob from the server
 * @param {CryptoKey} appPrivateKey - App's own ECDH private key
 * @returns {CryptoKey} User's ECDH private key (extractable)
 */
export async function unwrapPrivateKeyFromApp(encUserPrivkey, appPrivateKey) {
  const pkcs8b64 = await decryptField(encUserPrivkey, 'enc_user_privkey', { privateKey: appPrivateKey });
  const pkcs8 = fromBase64url(pkcs8b64);
  return crypto.subtle.importKey(
    'pkcs8', pkcs8,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

/**
 * Encrypt all declared encrypted fields in value_json.
 * encKeys = { publicKey: CryptoKey }
 * Already-encrypted values are skipped (partial updates don't re-encrypt).
 */
export async function encryptValueJson(valueJson, encryptedFields, encKeys) {
  if (!encryptedFields?.length || !encKeys?.publicKey) return valueJson;
  const result = { ...valueJson };
  for (const field of encryptedFields) {
    if (field in result && result[field] != null && !isEncrypted(result[field])) {
      result[field] = await encryptFieldV2(result[field], field, encKeys.publicKey);
    }
  }
  return result;
}

/**
 * Decrypt all declared encrypted fields in value_json.
 * encKeys = { privateKey, wrappingKey }
 */
export async function decryptValueJson(valueJson, encryptedFields, encKeys) {
  if (!encryptedFields?.length || !encKeys || !valueJson) return valueJson;
  const result = { ...valueJson };
  for (const field of encryptedFields) {
    if (field in result && isEncrypted(result[field])) {
      result[field] = await decryptField(result[field], field, encKeys);
    }
  }
  return result;
}
